<template>
  <div>
    <div v-if="!satellite" class="text-center py-5">
      <b-spinner></b-spinner>
    </div>

    <div v-else class="satellite-edit">
      <div class="satellite-header">
        <div class="satellite-header-left">
          <router-link to="/admin/developer/satellites" class="satellite-back" v-b-tooltip.hover title="Back to satellites">
            <b-icon icon="arrow-left-short" font-scale="1.4"></b-icon>
          </router-link>
          <div>
            <h4 class="satellite-title">{{ labelText(satellite.label, satellite.id) }}</h4>
            <code>{{ satellite.id }}</code>
          </div>
          <b-badge :variant="statusVariant(satellite.status)" pill>{{ satellite.status || 'draft' }}</b-badge>
          <b-badge :variant="healthVariant(satellite.health_status)" pill>{{ satellite.health_status || 'unknown' }}</b-badge>
        </div>

        <div class="satellite-header-right">
          <b-button size="sm" variant="outline-danger" @click="removeSatellite" :disabled="deletingSatellite">
            <b-icon icon="trash" class="mr-1"></b-icon>{{ $t('delete') }}
          </b-button>
          <b-button size="sm" variant="primary" @click="openSchemaModal">
            <b-icon icon="file-earmark-plus" class="mr-1"></b-icon>{{ $t('satellites.addSchemaVersion') }}
          </b-button>
        </div>
      </div>

      <div class="fg-versions">
        <div class="fg-version-stage">
          <span class="fg-version-label">Dev</span>
          <b-form-select v-model="devVersion" @change="deployVersion($event, 'dev')" :options="versionOptions" size="sm" class="fg-version-select" />
          <span class="fg-version-chip" :class="satellite.dev_version ? 'fg-version-chip--success' : 'fg-version-chip--inactive'">
            <b-icon :icon="satellite.dev_version ? 'check-circle-fill' : 'dash-circle'" class="fg-version-chip-icon"></b-icon>
            {{ satellite.dev_version ? $t('active') : $t('notActive') }}
          </span>
        </div>

        <div class="fg-version-promote">
          <b-icon icon="chevron-right" class="fg-version-arrow"></b-icon>
        </div>

        <div class="fg-version-stage">
          <span class="fg-version-label">Live</span>
          <b-form-select v-model="liveVersion" @change="deployVersion($event, 'live')" :options="versionOptions" size="sm" class="fg-version-select" />
          <span class="fg-version-chip" :class="satellite.live_version ? 'fg-version-chip--live' : 'fg-version-chip--inactive'">
            <b-icon :icon="satellite.live_version ? 'globe2' : 'dash-circle'" class="fg-version-chip-icon"></b-icon>
            {{ satellite.live_version ? $t('satellites.published') : $t('notPublished') }}
          </span>
        </div>
      </div>

      <b-tabs class="satellite-tabs" nav-class="satellite-tab-nav" content-class="satellite-tab-body">
        <b-tab active>
          <template #title><b-icon icon="sliders" class="mr-1"></b-icon>{{ $t('general') }}</template>
          <div class="satellite-tab-pad">
            <div class="satellite-setting">
              <label class="satellite-setting-label">{{ $t('satellites.endpoint') }}</label>
              <b-input :value="satellite.jsonrpc_endpoint_url" disabled />
            </div>
            <div class="satellite-setting" v-if="satellite.health_check_url">
              <label class="satellite-setting-label">{{ $t('satellites.healthcheck') }}</label>
              <b-input :value="satellite.health_check_url" disabled />
            </div>
            <div class="satellite-setting">
              <label class="satellite-setting-label">{{ $t('satellites.auth') }}</label>
              <div class="satellite-tags">
                <b-badge variant="secondary">{{ satellite.auth_type }}</b-badge>
                <b-badge v-if="satellite.auth_config && satellite.auth_config.header_name" variant="secondary">
                  {{ satellite.auth_config.header_name }}
                </b-badge>
              </div>
            </div>
          </div>
        </b-tab>

        <b-tab>
          <template #title><b-icon icon="layers" class="mr-1"></b-icon>{{ $t('satellites.versions') }}</template>
          <div class="satellite-tab-pad">
            <b-table small responsive hover :items="versions" :fields="versionFields" :busy="versionsLoading" show-empty>
              <template #cell(schema_version)="row">
                <strong>{{ row.item.schema_version || `v${row.item.version}` }}</strong>
              </template>
              <template #cell(version)="row">
                <code>#{{ row.item.version }}</code>
              </template>
              <template #cell(schema_hash)="row">
                <code>{{ shortHash(row.item.schema_hash) }}</code>
              </template>
              <template #cell(active)="row">
                <b-badge v-if="isVersionActive(row.item, 'dev')" variant="info" class="mr-1">dev</b-badge>
                <b-badge v-if="isVersionActive(row.item, 'live')" variant="success">live</b-badge>
              </template>
              <template #cell(actions)="row">
                <b-button size="sm" variant="outline-info" class="mr-1" @click="deployVersion(row.item.schema_version, 'dev')">
                  {{ $t('satellites.deployDev') }}
                </b-button>
                <b-button size="sm" variant="outline-success" @click="deployVersion(row.item.schema_version, 'live')">
                  {{ $t('satellites.deployLive') }}
                </b-button>
              </template>
            </b-table>
          </div>
        </b-tab>

        <b-tab>
          <template #title><b-icon icon="shield-lock" class="mr-1"></b-icon>{{ $t('satellites.permissions') }}</template>
          <div class="satellite-tab-pad">
            <div class="satellite-item-list" v-if="permissions.length">
              <div class="satellite-item" v-for="permission in permissions" :key="permission.permission_key">
                <strong>{{ permission.action }}</strong>
                <code>{{ permission.permission_key }}</code>
              </div>
            </div>
            <div v-else class="satellite-empty-inline">{{ $t('satellites.noPermissions') }}</div>
          </div>
        </b-tab>

        <b-tab>
          <template #title><b-icon icon="terminal" class="mr-1"></b-icon>{{ $t('satellites.commands') }}</template>
          <div class="satellite-tab-pad">
            <div class="satellite-command-controls">
              <b-form-select v-model="selectedCommand" :options="commandOptions" />
              <b-button variant="primary" @click="executeCommand" :disabled="!selectedCommand || executingCommand">
                <b-icon icon="play-fill" class="mr-1"></b-icon>{{ $t('satellites.execute') }}
              </b-button>
            </div>

            <b-row>
              <b-col md="6">
                <label class="satellite-setting-label">{{ $t('satellites.commandInput') }}</label>
                <CodeEditor
                  v-model="commandInput"
                  lang="application/json"
                  :withButton="false"
                  :height="220"
                  :options="{ mode: 'json', showFullscreenButton: true }"
                />
              </b-col>
              <b-col md="6">
                <label class="satellite-setting-label">{{ $t('satellites.commandResult') }}</label>
                <CodeEditor
                  :value="commandResult"
                  lang="application/json"
                  :withButton="false"
                  :height="220"
                  :readOnly="true"
                  :options="{ mode: 'json', readOnly: true, showFullscreenButton: true }"
                />
              </b-col>
            </b-row>

            <div class="satellite-item-list mt-3" v-if="activeSchema && activeSchema.jsonrpc_commands">
              <div class="satellite-item" v-for="command in activeSchema.jsonrpc_commands" :key="command.name">
                <strong>{{ command.name }}</strong>
                <span>{{ command.method }}</span>
                <b-badge variant="secondary">{{ command.permission_action || command.name }}</b-badge>
              </div>
            </div>
          </div>
        </b-tab>

        <b-tab>
          <template #title><b-icon icon="diagram3" class="mr-1"></b-icon>{{ $t('satellites.workflowNodes') }}</template>
          <div class="satellite-tab-pad">
            <div class="satellite-item-list" v-if="activeSchema && activeSchema.workflownodes && activeSchema.workflownodes.length">
              <div class="satellite-item" v-for="node in activeSchema.workflownodes" :key="node.name">
                <strong>{{ node.name }}</strong>
                <span>{{ labelText(node.label, node.command) }}</span>
                <b-badge variant="primary">{{ node.command }}</b-badge>
              </div>
            </div>
            <div v-else class="satellite-empty-inline">{{ $t('satellites.noWorkflowNodes') }}</div>
          </div>
        </b-tab>

        <b-tab>
          <template #title><b-icon icon="journal-text" class="mr-1"></b-icon>{{ $t('satellites.logs') }}</template>
          <div class="satellite-tab-pad">
            <div v-if="!satellite.logs_url" class="satellite-empty-inline">{{ $t('satellites.noLogsUrl') }}</div>
            <div v-else>
              <div class="satellite-logs-toolbar">
                <div class="satellite-logs-filters">
                  <b-form-group :label="$t('satellites.logsSince')" class="mb-0 satellite-logs-since">
                    <b-form-input v-model="logsSince" type="datetime-local" />
                  </b-form-group>
                  <b-form-group :label="$t('satellites.searchLogs')" class="mb-0 satellite-logs-search">
                    <b-input-group>
                      <b-input-group-prepend is-text>
                        <b-icon icon="search"></b-icon>
                      </b-input-group-prepend>
                      <b-form-input v-model.trim="logsSearch" :placeholder="$t('satellites.searchLogsPlaceholder')" />
                    </b-input-group>
                  </b-form-group>
                </div>
                <b-button variant="primary" @click="fetchLogs" :disabled="logsLoading">
                  <b-spinner v-if="logsLoading" small class="mr-1"></b-spinner>
                  <b-icon v-else icon="arrow-clockwise" class="mr-1"></b-icon>{{ $t('satellites.loadLogs') }}
                </b-button>
              </div>

              <div class="satellite-logs" v-if="filteredSatelliteLogs.length">
                <div class="satellite-log-line" v-for="(log, index) in filteredSatelliteLogs" :key="index">
                  <span class="satellite-log-time">{{ log.timestamp || '-' }}</span>
                  <span class="satellite-log-level" :class="`satellite-log-level--${log.level}`">{{ log.level }}</span>
                  <span class="satellite-log-message">{{ log.message }}</span>
                </div>
              </div>
              <div v-else-if="satelliteLogs.length" class="satellite-empty-inline">{{ $t('satellites.noLogsMatch') }}</div>
              <div v-else class="satellite-empty-inline">{{ $t('satellites.noLogs') }}</div>
            </div>
          </div>
        </b-tab>
      </b-tabs>
    </div>

    <main-dialog v-model="schemaModal" :title="$t('satellites.addSchemaVersion')" size="xl" hide-footer>
      <CodeEditor
        v-model="schemaJson"
        lang="application/json"
        :withButton="false"
        :height="520"
        :options="{ mode: 'json', showFullscreenButton: true }"
      />
      <div class="modal-actions mt-3">
        <b-button variant="outline-secondary" @click="schemaModal = false">{{ $t('cancel') }}</b-button>
        <b-button variant="primary" @click="createSchemaVersion" :disabled="savingSchema">
          {{ $t('satellites.createVersion') }}
        </b-button>
      </div>
    </main-dialog>
  </div>
</template>

<script>
import CodeEditor from '@/components/form/Editor/Code'
import { defaultSchema, healthVariant, labelText, shortHash, statusVariant } from './helpers'

export default {
  name: 'DeveloperSatellitesEdit',
  components: {
    CodeEditor
  },
  data() {
    return {
      satellite: null,
      versions: [],
      permissions: [],
      versionsLoading: false,
      logsLoading: false,
      satelliteLogs: [],
      logsSince: this.defaultLogsSince(),
      logsSearch: '',
      schemaModal: false,
      savingSchema: false,
      deletingSatellite: false,
      executingCommand: false,
      selectedCommand: '',
      devVersion: null,
      liveVersion: null,
      commandInput: '{\n  "inputs": {\n    "hello": "world"\n  }\n}',
      commandResult: '{}',
      schemaJson: '',
      versionFields: [
        { key: 'schema_version', label: 'Version' },
        { key: 'version', label: 'Revision' },
        { key: 'schema_hash', label: 'Hash' },
        { key: 'active', label: 'Active' },
        { key: 'created_at', label: 'Created' },
        { key: 'actions', label: '' }
      ]
    }
  },
  computed: {
    versionOptions() {
      return this.versions.map((item) => ({
        value: this.versionValue(item),
        text: item.schema_version || `v${item.version}`
      }))
    },
    activeSchema() {
      if (!this.satellite || !this.versions.length) return null
      const env = localStorage.getItem('current_env') || 'live'
      const preferred = env === 'dev' ? this.satellite.dev_version : this.satellite.live_version
      return this.findVersion(preferred) || this.versions[0]
    },
    commandOptions() {
      const commands = (this.activeSchema && this.activeSchema.jsonrpc_commands) || []
      return [
        { value: '', text: this.$t('satellites.selectCommand') },
        ...commands.map((command) => ({ value: command.name, text: command.name }))
      ]
    },
    filteredSatelliteLogs() {
      const query = this.logsSearch.trim().toLowerCase()
      if (!query) return this.satelliteLogs

      return this.satelliteLogs.filter((log) => {
        return [log.timestamp, log.level, log.message].some((value) => String(value || '').toLowerCase().includes(query))
      })
    }
  },
  methods: {
    healthVariant,
    labelText,
    shortHash,
    statusVariant,
    openSchemaModal() {
      this.schemaJson = JSON.stringify(defaultSchema(), null, 2)
      this.schemaModal = true
    },
    defaultLogsSince() {
      return new Date(Date.now() - 60 * 60 * 1000).toISOString().slice(0, 16)
    },
    logsSinceParam() {
      if (!this.logsSince) return ''
      const date = new Date(this.logsSince)
      if (Number.isNaN(date.getTime())) return this.logsSince
      return date.toISOString()
    },
    async removeSatellite() {
      if (!this.satellite) return
      const confirmed = await this.$mainDialog.msgBoxConfirm(this.$t('areYouSure'))
      if (!confirmed) return

      this.deletingSatellite = true
      const popup = this.$loadingPopup()

      this.$integrationApi
        .delete(`/satellites/templates/${this.satellite.id}`)
        .then(() => {
          this.$router.push('/admin/developer/satellites')
        })
        .catch((err) => {
          this.$debugError(err)
          this.$bvToast.toast(this.$t('satellites.failedToDelete'), {
            title: this.$t('error'),
            variant: 'danger'
          })
        })
        .finally(() => {
          popup.hide()
          this.deletingSatellite = false
        })
    },
    load() {
      this.$integrationApi
        .get(`/satellites/templates/${this.$route.params.id}`)
        .then((resp) => {
          this.satellite = resp.data
          this.devVersion = this.satellite.dev_version
          this.liveVersion = this.satellite.live_version
          return Promise.all([this.loadVersions(), this.loadPermissions()])
        })
        .catch((err) => {
          this.$debugError(err)
          this.$pageUnavailable()
        })
    },
    loadVersions() {
      if (!this.satellite) return Promise.resolve()
      this.versionsLoading = true
      return this.$integrationApi
        .get(`/satellites/templates/${this.satellite.id}/versions`)
        .then((resp) => {
          this.versions = resp.data || []
          this.syncVersionSelectors()
        })
        .catch((err) => {
          this.$debugError(err)
          this.versions = []
        })
        .finally(() => {
          this.versionsLoading = false
        })
    },
    versionValue(item) {
      if (!item) return ''
      return item.schema_version || String(item.version || '')
    },
    findVersion(version) {
      if (!version) return null
      return this.versions.find((item) => {
        return item.schema_version === version || String(item.version) === String(version)
      }) || null
    },
    displayVersion(version) {
      const item = this.findVersion(version)
      return item ? this.versionValue(item) : version
    },
    syncVersionSelectors() {
      this.devVersion = this.displayVersion(this.satellite && this.satellite.dev_version)
      this.liveVersion = this.displayVersion(this.satellite && this.satellite.live_version)
    },
    isVersionActive(item, env) {
      if (!this.satellite || !item) return false
      const version = env === 'dev' ? this.satellite.dev_version : this.satellite.live_version
      const active = this.findVersion(version)
      return !!active && this.versionValue(active) === this.versionValue(item)
    },
    loadPermissions() {
      if (!this.satellite) return Promise.resolve()
      return this.$integrationApi
        .get(`/satellites/templates/${this.satellite.id}/permissions`)
        .then((resp) => {
          this.permissions = resp.data || []
        })
        .catch(() => {
          this.permissions = []
        })
    },
    createSchemaVersion() {
      if (!this.satellite) return
      this.savingSchema = true

      let body
      try {
        body = JSON.parse(this.schemaJson)
      } catch (err) {
        this.$bvToast.toast(this.$t('satellites.invalidSchema'), {
          title: this.$t('error'),
          variant: 'danger'
        })
        this.savingSchema = false
        return
      }

      this.$integrationApi
        .post(`/satellites/templates/${this.satellite.id}/versions`, body)
        .then(() => {
          this.schemaModal = false
          this.$bvToast.toast(this.$t('satellites.versionCreated'), {
            title: this.$t('success'),
            variant: 'success'
          })
          return this.loadVersions()
        })
        .catch((err) => {
          this.$debugError(err)
          this.$bvToast.toast(this.$t('satellites.invalidSchema'), {
            title: this.$t('error'),
            variant: 'danger'
          })
        })
        .finally(() => {
          this.savingSchema = false
        })
    },
    deployVersion(schemaVersion, env) {
      if (!this.satellite || !schemaVersion) return
      const popup = this.$loadingPopup(this.$t('satellites.deploying'))

      this.$integrationApi
        .post(`/satellites/templates/${this.satellite.id}/deploy`, { env, schema_version: schemaVersion })
        .then((resp) => {
          this.satellite = resp.data
          this.syncVersionSelectors()
          this.$bvToast.toast(this.$t('satellites.deployed'), {
            title: this.$t('success'),
            variant: 'success'
          })
          return this.loadPermissions()
        })
        .catch((err) => {
          this.$debugError(err)
          this.$bvToast.toast(this.$t('satellites.failedToDeploy'), {
            title: this.$t('error'),
            variant: 'danger'
          })
        })
        .finally(() => {
          popup.hide()
        })
    },
    executeCommand() {
      if (!this.satellite || !this.selectedCommand) return
      this.executingCommand = true

      let parsed
      try {
        parsed = JSON.parse(this.commandInput || '{}')
      } catch (err) {
        this.commandResult = JSON.stringify({ message: err.message }, null, 2)
        this.executingCommand = false
        return
      }

      const body = {
        inputs: parsed.inputs || parsed,
        context: parsed.context || {}
      }

      this.$integrationApi
        .post(`/satellites/${this.satellite.id}/commands/${this.selectedCommand}/execute`, body)
        .then((resp) => {
          this.commandResult = JSON.stringify(resp.data, null, 2)
        })
        .catch((err) => {
          this.$debugError(err)
          const payload = err.response && err.response.data ? err.response.data : { message: err.message }
          this.commandResult = JSON.stringify(payload, null, 2)
          this.$bvToast.toast(this.$t('satellites.commandFailed'), {
            title: this.$t('error'),
            variant: 'danger'
          })
        })
        .finally(() => {
          this.executingCommand = false
        })
    },
    fetchLogs() {
      if (!this.satellite || !this.satellite.logs_url) return
      this.logsLoading = true

      this.$integrationApi
        .get(`/satellites/templates/${this.satellite.id}/logs`, {
          params: { since: this.logsSinceParam() }
        })
        .then((resp) => {
          this.satelliteLogs = this.normalizeLogs(resp.data)
        })
        .catch((err) => {
          this.$debugError(err)
          this.$bvToast.toast(this.$t('satellites.failedToLoadLogs'), {
            title: this.$t('error'),
            variant: 'danger'
          })
        })
        .finally(() => {
          this.logsLoading = false
        })
    },
    normalizeLogs(payload) {
      if (!payload) return []
      const items = Array.isArray(payload)
        ? payload
        : Array.isArray(payload.logs)
          ? payload.logs
          : Array.isArray(payload.entries)
            ? payload.entries
            : payload.logs
              ? [payload.logs]
              : []

      return items.map((item) => this.normalizeLogEntry(item)).slice(-1000)
    },
    normalizeLogEntry(item) {
      if (typeof item === 'string') {
        return { timestamp: '', level: 'info', message: item }
      }

      const timestamp = item.timestamp || item.time || item.ts || item.created_at || ''
      const level = String(item.level || item.severity || item.type || 'info').toLowerCase()
      const message = item.message || item.msg || item.text || JSON.stringify(item)

      return {
        timestamp,
        level,
        message
      }
    }
  },
  mounted() {
    this.load()
  }
}
</script>

<style scoped lang="scss">
.satellite-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.satellite-header-left,
.satellite-header-right,
.satellite-command-controls,
.modal-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.satellite-title {
  color: var(--text-primary);
  font-size: 20px;
  font-weight: 700;
  margin: 0;
}

.satellite-back {
  width: 34px;
  height: 34px;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
}

.satellite-setting-label {
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.fg-versions {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 0;
  border-bottom: 1px solid var(--border-default);
  overflow-x: auto;
  scrollbar-width: none;
  margin-bottom: 16px;
}

.fg-versions::-webkit-scrollbar {
  display: none;
}

.fg-version-stage {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.fg-version-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--text-muted, #949ba4);
  white-space: nowrap;
}

.fg-version-select {
  width: 220px;
  min-width: 180px;
  max-width: min(260px, 42vw);
  font-size: 12px;
  padding: 2px 24px 2px 8px;
  height: 28px;
}

.fg-version-promote {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.fg-version-arrow {
  color: var(--text-muted, #949ba4);
  font-size: 11px;
  opacity: 0.6;
}

.fg-version-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: var(--radius-sm, 6px);
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  line-height: 1.3;
}

.fg-version-chip-icon {
  font-size: 14px;
}

.fg-version-chip--success {
  background: rgba(72, 187, 120, 0.12);
  color: var(--success-start, #48bb78);
  border: 1px solid rgba(72, 187, 120, 0.25);
}

.fg-version-chip--live {
  background: rgba(108, 128, 255, 0.12);
  color: var(--primary-start, #6c80ff);
  border: 1px solid rgba(108, 128, 255, 0.25);
}

.fg-version-chip--inactive {
  background: var(--bg-card-secondary, rgba(255, 255, 255, 0.05));
  color: var(--text-muted, #949ba4);
  border: 1px solid var(--border-default);
}

.satellite-tabs {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 8px;
}

.satellite-tab-pad {
  padding: 18px;
}

.satellite-setting {
  margin-bottom: 16px;
}

.satellite-setting-label {
  display: block;
  margin-bottom: 7px;
}

.satellite-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.satellite-item-list {
  display: grid;
  gap: 8px;
}

.satellite-item {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  padding: 10px 12px;
}

.satellite-empty-inline {
  color: var(--text-muted);
  padding: 32px 12px;
  text-align: center;
}

.satellite-command-controls {
  margin-bottom: 14px;
}

.satellite-logs-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.satellite-logs-filters {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  min-width: 0;
}

.satellite-logs-since {
  flex: 0 0 240px;
}

.satellite-logs-search {
  flex: 0 1 360px;
  min-width: 260px;
}

.satellite-logs {
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  max-height: 520px;
  overflow: auto;
  padding: 8px 0;
}

.satellite-log-line {
  display: grid;
  grid-template-columns: minmax(150px, 220px) 84px minmax(0, 1fr);
  gap: 10px;
  padding: 6px 12px;
  font-family: Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: 12px;
  line-height: 1.5;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);

  &:last-child {
    border-bottom: none;
  }
}

.satellite-log-time {
  color: var(--text-muted);
  white-space: nowrap;
}

.satellite-log-level {
  color: #45b8ff;
  font-weight: 700;
  text-transform: uppercase;

  &--error,
  &--fatal {
    color: #e55353;
  }

  &--warn,
  &--warning {
    color: #f9b115;
  }

  &--debug {
    color: #949ba4;
  }
}

.satellite-log-message {
  color: var(--text-primary);
  min-width: 0;
  overflow-wrap: anywhere;
}

@media (max-width: 991px) {
  .satellite-header,
  .satellite-header-left,
  .satellite-header-right,
  .satellite-command-controls,
  .satellite-logs-toolbar,
  .satellite-logs-filters {
    align-items: stretch;
    flex-direction: column;
  }

  .satellite-logs-since,
  .satellite-logs-search {
    flex: 1 1 auto;
    min-width: 0;
  }

  .satellite-log-line {
    grid-template-columns: 1fr;
  }
}
</style>
